import React, { useEffect, useRef } from "react";

/**
 * MatrixRain — backdrop continuo, MUY sutil y discreto.
 *
 * La animación corre todo el tiempo pero con presencia mínima:
 *  - Pocas columnas, separadas entre sí (densidad baja).
 *  - Velocidad lenta.
 *  - Opacidad muy reducida (apenas perceptible).
 *  - Sin comets brillantes, sin acentos verdes.
 *  - Estela larga y suave para que cada caracter se desvanezca con
 *    gracia en lugar de "rayar" la pantalla.
 *
 *  Skip en móvil y prefers-reduced-motion. Cap a 24 fps.
 */
const CHARS_KATA  = "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ";
const CHARS_NUM   = "0123456789";
const CHARS_LATIN = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const POOL        = CHARS_KATA + CHARS_NUM + CHARS_LATIN;

const pickChar = () => POOL[(Math.random() * POOL.length) | 0];
const rand     = (min, max) => Math.random() * (max - min) + min;

export default function MatrixRain({
  fontSize = 13,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const mql = window.matchMedia("(max-width: 767px), (prefers-reduced-motion: reduce)");
    if (mql.matches) { canvas.style.display = "none"; return; }

    const ctx = canvas.getContext("2d", { alpha: true });
    let columns = [];
    let raf = 0;
    let lastDraw = 0;
    const FPS_INTERVAL = 1000 / 24;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Densidad muy baja: ~1 columna cada ~110 px (en lugar de 1 por font-size).
      // Eso deja la pantalla con sólo unas 15-18 columnas en 1920px.
      const slotPx = 110;
      const colCount = Math.max(6, Math.floor(w / slotPx));
      const colStep  = w / colCount;
      columns = new Array(colCount).fill(0).map((_, i) => ({
        x: Math.round(i * colStep + colStep / 2 + rand(-12, 12)),
        y: -rand(0, h),
        speed: rand(0.35, 0.7),    // velocidad lenta
        len: 14 + ((Math.random() * 10) | 0),
        char: pickChar(),
        scramble: 0,
      }));
    };

    const draw = (t) => {
      if (t - lastDraw < FPS_INTERVAL) { raf = requestAnimationFrame(draw); return; }
      lastDraw = t;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      // Fade del frame anterior — translúcido para crear la estela larga y suave.
      ctx.fillStyle = "rgba(6,8,13,0.22)";
      ctx.fillRect(0, 0, w, h);

      ctx.font = `${fontSize}px 'JetBrains Mono', 'Chakra Petch', monospace`;
      ctx.textBaseline = "top";

      for (const col of columns) {
        if (col.scramble++ % 5 === 0) col.char = pickChar();

        // Head — apenas perceptible, casi blanco tenue
        ctx.fillStyle = "rgba(220,255,255,0.22)";
        ctx.fillText(col.char, col.x, col.y);

        // Trail — cyan muy diluido
        for (let i = 1; i < col.len; i++) {
          const ty = col.y - i * fontSize;
          if (ty < -fontSize) break;
          const a = (1 - i / col.len) * 0.18;
          ctx.fillStyle = `rgba(0,220,255,${a})`;
          ctx.fillText(POOL[(Math.random() * POOL.length) | 0], col.x, ty);
        }

        col.y += col.speed * fontSize * 0.5;
        if (col.y > h + col.len * fontSize) {
          col.y = -rand(40, h * 0.6);
          col.speed = rand(0.35, 0.7);
          col.len = 14 + ((Math.random() * 10) | 0);
        }
      }

      raf = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener("resize", resize);
    raf = requestAnimationFrame(draw);

    const onVis = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else {
        lastDraw = 0;
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fontSize]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      data-testid="matrix-rain"
      className="fixed inset-0 w-screen h-screen pointer-events-none"
      style={{ zIndex: 0, mixBlendMode: "screen", opacity: 0.45 }}
    />
  );
}
